import { useEffect, useRef, useState } from 'preact/hooks';
import type { Stop, StopDetail } from '../../../../arcticapi/types';
import { useStopDeparturesDetails } from '../../../../data/useStopDeparturesDetails';
import { useAutoRefreshCountdown } from '../useAutoRefreshCountdown';
import { StopDetailsPage } from './StopDetailsPage';
import { useWidgetAnalytics } from '../../../../analytics/useWidgetAnalytics';

interface StopDeparturesDetailsPageProps {
  selectedStopAtcoCode?: string | null;
  departuresHrefOverride?: string | null;
  fallbackStop?: Stop | StopDetail | null;
  fallbackStopName?: string | null;
}

function formatUpdatedTime(timestamp: number | null): string | null {
  if (timestamp === null) {
    return null;
  }

  const date = new Date(timestamp);
  const hours = `${date.getHours()}`.padStart(2, '0');
  const minutes = `${date.getMinutes()}`.padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * Widget details-page wrapper for stop departures.
 *
 * Adds widget-specific UI behaviour such as auto-refresh and countdown state,
 * while the data hook stays focused on raw stop departures data.
 */
export function StopDeparturesDetailsPage({
  selectedStopAtcoCode,
  departuresHrefOverride,
  fallbackStop,
  fallbackStopName,
}: StopDeparturesDetailsPageProps) {
  const { track } = useWidgetAnalytics();
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const [countdownStart, setCountdownStart] = useState<number | null>(null);
  const departuresWasLoadingRef = useRef(false);

  const {
    departuresLoading,
    departuresError,
    departuresStop,
    departuresStopName,
    departuresDisruption,
    departuresStreetViewLink,
    departuresLastUpdatedAt,
    departures,
    refreshDepartures,
  } = useStopDeparturesDetails({
    selectedStopAtcoCode,
    departuresHrefOverride,
    fallbackStop,
    fallbackStopName,
    autoRefreshEnabled,
  });

  const countdownSecondsRemaining = useAutoRefreshCountdown({
    autoRefreshEnabled,
    lastUpdatedAt: autoRefreshEnabled ? countdownStart : null,
  });

  useEffect(() => {
    if (autoRefreshEnabled) {
      setCountdownStart(Date.now());
    }
  }, [autoRefreshEnabled]);

  useEffect(() => {
    if (departuresLoading) {
      departuresWasLoadingRef.current = true;
      return;
    }
    if (!departuresWasLoadingRef.current) return;
    departuresWasLoadingRef.current = false;

    const atcoCode =
      (departuresStop && 'atcoCode' in departuresStop
        ? departuresStop.atcoCode
        : null) ??
      selectedStopAtcoCode ??
      '';
    const lineIds = [
      ...new Set(departures.map((d) => d.line?.name).filter(Boolean)),
    ] as string[];
    const operatorNames = [
      ...new Set(departures.map((d) => d.line?.operator).filter(Boolean)),
    ] as string[];

    track({
      name: 'departures_board_loaded',
      activity: 'departures',
      page: 'details',
      payload: {
        status: departuresError
          ? 'error'
          : departures.length === 0
            ? 'empty'
            : 'success',
        atcoCode,
        departureCount: departures.length,
        lineIds,
        operatorCodes: [],
        operatorNames,
        ...(departuresError ? { errorMessage: departuresError.message } : {}),
      },
    });
  }, [
    departuresLoading,
    departures,
    departuresError,
    departuresStop,
    selectedStopAtcoCode,
    track,
  ]);

  const departuresLastUpdatedTime = formatUpdatedTime(departuresLastUpdatedAt);

  const handleAutoRefreshChange = (enabled: boolean) => {
    track({
      name: 'departures_auto_refresh_changed',
      activity: 'departures',
      page: 'details',
      payload: { enabled },
    });
    setAutoRefreshEnabled(enabled);
  };

  const handleRefresh = () => {
    track({
      name: 'departures_board_refreshed',
      activity: 'departures',
      page: 'details',
      payload: {},
    });
    refreshDepartures();
  };

  return (
    <StopDetailsPage
      departuresStop={departuresStop}
      departuresStopName={departuresStopName}
      departuresDisruption={departuresDisruption}
      departuresStreetViewLink={departuresStreetViewLink}
      departuresLoading={departuresLoading}
      departuresError={departuresError}
      departures={departures}
      autoRefreshEnabled={autoRefreshEnabled}
      onAutoRefreshChange={handleAutoRefreshChange}
      onRefresh={handleRefresh}
      countdownSecondsRemaining={countdownSecondsRemaining}
      departuresLastUpdatedTime={departuresLastUpdatedTime}
    />
  );
}
